import { useState, useCallback, useEffect } from 'react';
import { useCalendarStore } from '../../lib/stores/calendarStore';
import { useErrorHandler } from '../../lib/hooks/useErrorHandler';
import type { CalendarEvent } from '../../lib/types';

/**
 * Custom hook for working with Google Calendar integration
 * Provides error handling, loading state management, and retry logic
 */
export function useGoogleCalendar() {
  const {
    googleEvents,
    googleLoading,
    googleError,
    fetchGoogleEvents,
    createGoogleEvent,
    updateGoogleEvent,
    deleteGoogleEvent,
    getGoogleEvent,
    resetErrors,
    clearSyncToken,
    calendarSources
  } = useCalendarStore();
  
  const { handleError } = useErrorHandler();
  const [localLoading, setLocalLoading] = useState<boolean>(false);

  // Handle errors in the store
  useEffect(() => {
    if (googleError) {
      // Don't show toast for "no Google connected" error - handled by UI prompt in CalendarSourcesSidebar
      const isNoGoogleConnected =
        googleError.toLowerCase().includes('does not have a connected google') ||
        googleError.toLowerCase().includes('no google refresh token') ||
        googleError.toLowerCase().includes('user does not have a connected') ||
        googleError.toLowerCase().includes('edge function returned a non-2xx') ||
        googleError.toLowerCase().includes('404') ||
        googleError.toLowerCase().includes('google workspace is not properly configured');

      if (!isNoGoogleConnected) {
        // We've already formatted the error message in the store
        handleError({ message: googleError }, 'Google Calendar error');
      }
    }
  }, [googleError, handleError]);

  /**
   * Fetches Google Calendar events with error handling
   * @param options.skipCalendarListFetch - Se true, salta il fetch della lista calendari (usa per navigazione)
   * @param options.forceRefresh - Se true, bypassa il cooldown e usa IDs aggiornati (usa per toggle calendario)
   */
  const fetchEvents = useCallback(async (
    startDate: Date,
    endDate: Date,
    options?: { skipCalendarListFetch?: boolean; forceRefresh?: boolean }
  ) => {
    try {
      setLocalLoading(true);
      await fetchGoogleEvents(startDate, endDate, options);
    } catch (error) {
      handleError(error, 'Failed to fetch Google Calendar events');
    } finally {
      setLocalLoading(false);
    }
  }, [fetchGoogleEvents, handleError]);

  /**
   * Creates a Google Calendar event with error handling
   */
  const createEvent = useCallback(async (
    calendarId: string,
    event: Omit<CalendarEvent, 'id' | 'created_at' | 'created_by'>,
    assignedUserId?: string
  ) => {
    try {
      setLocalLoading(true);
      const result = await createGoogleEvent(calendarId, event, assignedUserId);
      if (!result) {
        throw new Error('Failed to create event');
      }
      return result;
    } catch (error) {
      handleError(error, 'Failed to create Google Calendar event');
      return null;
    } finally {
      setLocalLoading(false);
    }
  }, [createGoogleEvent, handleError]);

  /**
   * Updates a Google Calendar event with error handling
   */
  const updateEvent = useCallback(async (
    eventId: string, 
    calendarId: string, 
    event: Partial<CalendarEvent>
  ) => {
    try {
      setLocalLoading(true);
      const result = await updateGoogleEvent(eventId, calendarId, event);
      if (!result) {
        throw new Error('Failed to update event');
      }
      return result;
    } catch (error) {
      handleError(error, 'Failed to update Google Calendar event');
      return null;
    } finally {
      setLocalLoading(false);
    }
  }, [updateGoogleEvent, handleError]);

  /**
   * Deletes a Google Calendar event with error handling
   */
  const deleteEvent = useCallback(async (eventId: string, calendarId: string) => {
    try {
      setLocalLoading(true);
      const result = await deleteGoogleEvent(eventId, calendarId);

      // If result is false, it means there was a real error (not 404)
      if (result === false) {
        throw new Error('Failed to delete event');
      }

      // result is true - success (either deleted or already gone)
      return result;
    } catch (error) {
      // Only show error toast and re-throw for real errors
      // 404 cases are already handled in calendarStore and return true
      handleError(error, 'Failed to delete Google Calendar event');
      throw error;
    } finally {
      setLocalLoading(false);
    }
  }, [deleteGoogleEvent, handleError]);

  /**
   * Gets a single Google Calendar event with full details (including attendees)
   */
  const getEvent = useCallback(async (eventId: string, calendarId: string) => {
    try {
      setLocalLoading(true);
      const result = await getGoogleEvent(eventId, calendarId);
      if (!result) {
        throw new Error('Failed to get event');
      }
      return result;
    } catch (error) {
      handleError(error, 'Failed to get Google Calendar event');
      return null;
    } finally {
      setLocalLoading(false);
    }
  }, [getGoogleEvent, handleError]);

  /**
   * Refreshes calendar data by clearing sync token and fetching again
   */
  const refreshCalendar = useCallback(async (startDate: Date, endDate: Date) => {
    clearSyncToken();
    resetErrors();
    return fetchEvents(startDate, endDate);
  }, [clearSyncToken, resetErrors, fetchEvents]);

  /**
   * Gets enabled Google calendar sources
   */
  const getEnabledGoogleCalendars = useCallback(() => {
    return calendarSources.filter(
      source => source.type === 'google' && source.enabled && source.googleCalendarId
    );
  }, [calendarSources]);

  return {
    events: googleEvents,
    loading: googleLoading || localLoading,
    fetchEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    getEvent,
    refreshCalendar,
    getEnabledGoogleCalendars,
    calendarSources: calendarSources.filter(source => source.type === 'google')
  };
}